import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { useEditorToolbar } from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { PasteOptionsToolbarSharedState } from '@atlaskit/editor-plugin-paste-options-toolbar';
import { getSingleSmartLinkUrlFromSlice } from '@atlaskit/editor-plugin-paste-options-toolbar/current-pasted-smart-link';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn } from '../../types/types';
import { createSmartLinkSummarizeCommand } from '../smartlink-summarize/create-smartlink-summarize-command';

const SMART_LINK_NODE_NAMES = new Set(['embedCard', 'blockCard', 'inlineCard']);

const getPastedSmartLinkNodeType = (
	state: EditorState,
	pasteStartPos: number | undefined,
): NodeType => {
	const { schema, doc } = state;
	const fallback = schema.nodes.inlineCard;
	if (pasteStartPos === undefined || pasteStartPos < 0 || pasteStartPos >= doc.content.size) {
		return fallback;
	}
	const node = doc.nodeAt(pasteStartPos);
	if (!node || !SMART_LINK_NODE_NAMES.has(node.type.name)) {
		return fallback;
	}
	return node.type;
};

export const SmartLinkAddSummaryPasteMenuItem = ({
	aiGlobalOptIn,
	api,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
}): React.JSX.Element => {
	const { editorView } = useEditorToolbar();
	const { formatMessage, locale } = useIntl();
	const currentPastedLink = useSharedPluginStateWithSelector(
		api,
		['pasteOptionsToolbarPlugin', 'paste'],
		(states) => {
			const pluginState = states.pasteOptionsToolbarPluginState as
				| PasteOptionsToolbarSharedState
				| undefined;
			const lastContentPasted = states.pasteState?.lastContentPasted;
			return {
				pasteEndPos: pluginState?.pasteEndPos ?? lastContentPasted?.pasteEndPos,
				pasteStartPos: pluginState?.pasteStartPos ?? lastContentPasted?.pasteStartPos,
				url: getSingleSmartLinkUrlFromSlice(lastContentPasted?.pastedSlice),
			};
		},
	);

	const handleClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			if (!editorView || !currentPastedLink.url) {
				return;
			}
			const isKeyboardTrigger = 'key' in event;
			const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';
			// The pasted Smart Link may resolve from inlineCard into a block-level
			// embedCard/blockCard, so use the actual node type at the paste position.
			const nodeType = getPastedSmartLinkNodeType(
				editorView.state,
				currentPastedLink.pasteStartPos,
			);
			if (!nodeType) {
				return;
			}
			createSmartLinkSummarizeCommand({
				aiGlobalOptIn,
				decorateDeselectTransaction: api?.card?.actions.hideLinkToolbar,
				fireAnalyticsEvent: api?.analytics?.actions.fireAnalyticsEvent,
				lastTriggeredFrom: 'pasteActionsMenu',
				locale,
				nodeRange:
					currentPastedLink.pasteStartPos !== undefined &&
					currentPastedLink.pasteEndPos !== undefined
						? {
								from: currentPastedLink.pasteStartPos,
								to: currentPastedLink.pasteEndPos,
							}
						: undefined,
				nodeType,
				smartlinkUrl: currentPastedLink.url,
				triggeredForMark: editorView.state.schema.marks.link,
				triggerMethod,
			})(editorView.state, editorView.dispatch, editorView);
		},
		[aiGlobalOptIn, api, currentPastedLink, editorView, locale],
	);

	return (
		<ToolbarDropdownItem
			testId="ai-paste-menu__smart-link-add-summary"
			elemBefore={<AiGenerativeTextSummaryIcon size="medium" label="" />}
			onClick={handleClick}
		>
			{formatMessage({
				defaultMessage: 'Add summary',
				description: 'Paste actions menu item that adds an AI summary below a Smart Link.',
				id: 'fabric.editor.ai.aiPasteMenu.addSummary',
			})}
		</ToolbarDropdownItem>
	);
};
